import { Controller } from "@hotwired/stimulus"

// Campaign picker dropdown supporting:
//   - Instant display of locally-known campaigns
//   - Lazy-loaded HeyReach campaigns fetched on first open
//   - Inline search filter
//   - Hidden input management for form submission
//
// Required targets in HTML:
//   trigger, label, dropdown, search,
//   localList, heyreachSection, heyreachList, heyreachStatus,
//   campaignIdInput, heyreachCampaignIdInput, heyreachCampaignNameInput
//
// Required values:
//   url  — the endpoint for fetching HeyReach campaigns (JSON)

// Platform statuses arrive lowercased from the JSON endpoint. The real enum is
// DRAFT / STARTING / IN_PROGRESS / PAUSED / FINISHED / CANCELED / FAILED /
// SCHEDULED — there is no "active" and no "completed", which is why the previous
// `status === "active"` colour test never once matched.
//
// DRAFT IS SELECTABLE, and deliberately so. A draft campaign cannot be started
// while it is empty and cannot be added to while it is a draft, so refusing it
// here left an imported campaign with no reachable first move at all. Choosing
// it runs the seed-then-start route (EnrolmentPlan#seed_and_launch?): the lead
// is put on the backing list, which a draft accepts, and the campaign is then
// started non-empty and read back. Paused and finished are likewise selectable
// — both are woken by the wake flags on the enrol call itself.
//
// Cancelled and failed stay unselectable because they are genuinely terminal:
// the enrol refuses them permanently and no click can change that.
const SELECTABLE_STATUSES = ["in_progress", "starting", "scheduled", "paused", "finished", "draft"]

const UNSELECTABLE_REASON = {
  canceled: "has been cancelled",
  failed:   "has stopped with an error"
}

// Shown on a selectable campaign whose state the click will change, so the row
// reads as what will happen rather than as a bare status.
const SELECTABLE_HINT = {
  draft:    "will start when you add this person",
  paused:   "will resume when you add this person",
  finished: "will restart when you add this person"
}

const STATUS_LABEL = {
  in_progress: "Running",
  starting:    "Starting",
  scheduled:   "Scheduled",
  paused:      "Paused",
  finished:    "Finished",
  draft:       "Not launched",
  canceled:    "Cancelled",
  failed:      "Failed"
}

const statusLabel = status => STATUS_LABEL[status] || status

const statusColour = status => {
  if (status === "in_progress" || status === "starting") return "bg-green-100 text-green-700"
  if (status === "paused" || status === "scheduled")     return "bg-yellow-100 text-yellow-700"
  return "bg-gray-100 text-gray-500"
}

export default class extends Controller {
  static targets = [
    "trigger", "label", "dropdown", "search",
    "localList", "heyreachSection", "heyreachList", "heyreachStatus",
    "campaignIdInput", "heyreachCampaignIdInput", "heyreachCampaignNameInput"
  ]

  static values = {
    url:    String,
    loaded: { type: Boolean, default: false }
  }

  connect() {
    this.handleOutsideClick = this.handleOutsideClick.bind(this)
    document.addEventListener("click", this.handleOutsideClick)
  }

  disconnect() {
    document.removeEventListener("click", this.handleOutsideClick)
  }

  // ── Open / close ──────────────────────────────────────────────────────────

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.isOpen() ? this.close() : this.open()
  }

  open() {
    this.dropdownTarget.classList.remove("hidden")
    this.searchTarget.focus()
    if (!this.loadedValue) this.fetchHeyreachCampaigns()
  }

  close() {
    this.dropdownTarget.classList.add("hidden")
    this.searchTarget.value = ""
    this.filterOptions("")
  }

  isOpen() {
    return !this.dropdownTarget.classList.contains("hidden")
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target) && this.isOpen()) this.close()
  }

  // ── Selection ────────────────────────────────────────────────────────────

  selectLocal(event) {
    const btn  = event.currentTarget
    const id   = btn.dataset.campaignId
    const name = btn.dataset.campaignName

    this.campaignIdInputTarget.value          = id
    this.heyreachCampaignIdInputTarget.value  = ""
    this.heyreachCampaignNameInputTarget.value = ""

    this.labelTarget.textContent = name
    this.labelTarget.title = name
    this.triggerTarget.classList.remove("text-gray-400")
    this.triggerTarget.classList.add("text-gray-900")

    this.markSelected(btn)
    this.close()
  }

  selectHeyreach(event) {
    const btn    = event.currentTarget
    const hrId   = btn.dataset.heyreachId
    const hrName = btn.dataset.heyreachName

    this.campaignIdInputTarget.value           = ""
    this.heyreachCampaignIdInputTarget.value   = hrId
    this.heyreachCampaignNameInputTarget.value = hrName

    this.labelTarget.textContent = hrName
    this.labelTarget.title = hrName
    this.triggerTarget.classList.remove("text-gray-400")
    this.triggerTarget.classList.add("text-gray-900")

    this.markSelected(btn)
    this.close()
  }

  markSelected(activeBtn) {
    this.element.querySelectorAll("[data-picker-option]").forEach(el => {
      el.classList.remove("bg-indigo-50", "text-indigo-700")
      el.classList.add("text-gray-700")
    })
    activeBtn.classList.add("bg-indigo-50", "text-indigo-700")
    activeBtn.classList.remove("text-gray-700")
  }

  // ── Search filter ─────────────────────────────────────────────────────────

  filter(event) {
    this.filterOptions(event.currentTarget.value)
  }

  filterOptions(query) {
    const q = query.toLowerCase().trim()
    this.element.querySelectorAll("[data-picker-option]").forEach(el => {
      const name = (el.dataset.campaignName || el.dataset.heyreachName || "").toLowerCase()
      el.classList.toggle("hidden", q.length > 0 && !name.includes(q))
    })
    // Show/hide section headers based on visible options
    this.toggleSectionVisibility(this.localListTarget)
    if (this.loadedValue) this.toggleSectionVisibility(this.heyreachListTarget)
  }

  toggleSectionVisibility(listEl) {
    const section = listEl.closest("[data-section]")
    if (!section) return
    const anyVisible = Array.from(listEl.querySelectorAll("[data-picker-option]"))
      .some(el => !el.classList.contains("hidden"))
    section.classList.toggle("hidden", !anyVisible)
  }

  // ── HeyReach lazy fetch ───────────────────────────────────────────────────

  async fetchHeyreachCampaigns() {
    this.heyreachStatusTarget.textContent = "Loading…"
    this.heyreachSectionTarget.classList.remove("hidden")

    try {
      const response = await fetch(this.urlValue, {
        headers: {
          "Accept":            "application/json",
          "X-Requested-With":  "XMLHttpRequest",
          "X-CSRF-Token":      document.querySelector('meta[name="csrf-token"]')?.content
        }
      })

      const json = await response.json()

      if (json.error && !json.campaigns?.length) {
        this.heyreachStatusTarget.textContent = json.error
        return
      }

      const unlinked = json.campaigns.filter(c => !c.linked)

      if (unlinked.length === 0) {
        this.heyreachStatusTarget.textContent = "All LinkedIn campaigns are already linked above."
        return
      }

      this.heyreachStatusTarget.textContent = ""
      this.renderHeyreachCampaigns(unlinked)
      this.loadedValue = true

    } catch (e) {
      this.heyreachStatusTarget.textContent = "Could not load LinkedIn campaigns."
    }
  }

  renderHeyreachCampaigns(campaigns) {
    const list = this.heyreachListTarget
    list.innerHTML = ""

    campaigns.forEach(c => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.heyreachId    = c.id
      btn.dataset.heyreachName  = c.name
      btn.dataset.pickerOption  = "true"

      // Only the terminal states are disabled — see SELECTABLE_STATUSES. A
      // dormant or not-yet-started campaign is offered, with a title saying what
      // the click will do to it, because the enrol brings it up as part of
      // adding the lead. An unrecognised status falls through to unselectable:
      // the enrol whitelists states, so a status we do not know is one it would
      // refuse.
      const selectable = SELECTABLE_STATUSES.includes(c.status)

      if (selectable) btn.dataset.action = "click->campaign-picker#selectHeyreach"
      btn.disabled = !selectable

      btn.className = "w-full flex items-center justify-between gap-2 px-3 py-2 text-sm rounded-md text-left " +
        (selectable ? "text-gray-700 hover:bg-gray-50 cursor-pointer" : "text-gray-400 cursor-not-allowed")

      const hint = selectable ? SELECTABLE_HINT[c.status] : (UNSELECTABLE_REASON[c.status] || "cannot accept new leads")
      btn.title = hint ? `${c.name} — ${hint}` : c.name

      btn.innerHTML = `
        <span class="truncate flex-1 min-w-0">${this.escapeHtml(c.name)}</span>
        <span class="flex-shrink-0 inline-flex items-center gap-1">
          <span class="inline-block px-1.5 py-0.5 rounded text-xs font-medium ${statusColour(c.status)}">${this.escapeHtml(statusLabel(c.status))}</span>
          <span class="text-xs text-gray-400 italic">LinkedIn</span>
        </span>
      `
      list.appendChild(btn)
    })
  }

  escapeHtml(str) {
    return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;")
  }
}
